import { Contact, FunnelStage, Lead } from '../../../types';
import { notify } from '../../../utils/toast';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[LeadForm]');

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validação campo a campo para criação/duplicação de negociação (Meta/contato/título). */
/* eslint-disable complexity -- validações UX independentes por campo */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload de formulários/modais heterogêneo
export const validateLeadForm = (formData: any, contacts: Contact[]) => {
    if (formData && formData.nativeEvent) {
        log.error('ERRO CRÍTICO: Recebido evento em vez de dados!');
        notify.error('Erro ao processar formulário');
        return null;
    }

    // Busca o contato selecionado (pode não estar na lista local ainda após criação recente)
    const contact = contacts.find(c => c.id === formData.contactId);

    // Validação: título é obrigatório, com fallback para nome do contato
    // Sanitização simples: remove tags HTML para evitar XSS
    let title = formData?.title ? String(formData.title).replace(/<[^>]*>?/gm, '').trim() : '';

    // Se título estiver vazio, usa o nome do contato como fallback
    if (!title || title.length === 0) {
        if (contact?.name && contact.name.trim().length > 0) {
            title = contact.name.trim();
        } else {
            log.error('Título da negociação é obrigatório e contato não tem nome');
            notify.error('Por favor, preencha o título da negociação ou selecione um contato com nome');
            return null;
        }
    }

    // Validação: título deve ter pelo menos 2 caracteres
    if (title.length < 2) {
        log.error('Título da negociação deve ter pelo menos 2 caracteres');
        notify.error('O título da negociação deve ter pelo menos 2 caracteres');
        return null;
    }

    // Validação: contato salvo é obrigatório (UUID persistido, não texto digitado)
    const contactId = formData.contactId ? String(formData.contactId).trim() : '';
    if (!contactId || !UUID_REGEX.test(contactId)) {
        log.error('Contato é obrigatório para criar negociação (ID inválido ou ausente)');
        notify.error('Selecione ou salve um contato na lista antes de criar a negociação.');
        return null;
    }

    // Validação: origin é obrigatório e deve ser selecionado pelo usuário
    if (!formData.source || formData.source.trim() === '') {
        log.error('Origem da negociação é obrigatória');
        notify.error('Por favor, selecione a origem da negociação');
        return null;
    }

    const origin = formData.source.trim();

    return {
        title,
        origin,
        contact,
        value: parseFloat(String(formData.value || '0')) || 0,
        agent: formData.assignedTo ? String(formData.assignedTo) : null,
        contactId
    };
};
/* eslint-enable complexity */

export type ValidatedLeadForm = NonNullable<ReturnType<typeof validateLeadForm>>;

/** Monta entidade de negociação nova a partir do formulário validado (mesma regra do modal Kanban). */
export function buildNewLeadFromValidated(
    validated: ValidatedLeadForm,
    stageToUse: string
): Lead {
    return {
        id: `temp_${Date.now()}`,
        name: validated.title,
        email: String(validated.contact?.email || ''),
        whatsApp: String(validated.contact?.whatsapp || validated.contact?.phone || ''),
        value: validated.value,
        stage: String(stageToUse),
        status: 'open',
        agent: validated.agent,
        tags: [],
        origin: validated.origin,
        date: new Date()
            .toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: '2-digit' })
            .replace('.', ''),
        entryDate: new Date().toISOString(),
        contactId: validated.contactId
    };
}

/**
 * Persiste nova negociação a partir do payload do modal / duplicação.
 * Centraliza validação + estágio + save (DRY com useKanban.handleSaveLeadFromModal).
 */
export async function submitNewLeadFromModalForm(
    formData: Record<string, unknown>,
    contacts: Contact[],
    funnelStages: FunnelStage[],
    preselectedStageId: string | null | undefined,
    saveLead: (lead: Lead) => Promise<Lead | null>
): Promise<boolean> {
    const validated = validateLeadForm(formData, contacts);
    if (!validated) return false;

    const stageToUse =
        (typeof formData.stage === 'string' && formData.stage) ||
        preselectedStageId ||
        funnelStages[0]?.id ||
        '';

    if (!stageToUse) {
        log.error('Nenhuma etapa de funil disponível');
        notify.error('Crie pelo menos uma etapa no funil antes de criar negociações');
        return false;
    }

    const leadToSave = buildNewLeadFromValidated(validated, stageToUse);
    const saved = await saveLead(leadToSave);
    return !!saved;
}

/** Duplica uma negociação existente (mesma lógica de useKanbanPage.handleDuplicateLead). */
export async function duplicateLeadAsCopy(
    source: Lead,
    contacts: Contact[],
    funnelStages: FunnelStage[],
    preselectedStageId: string | null | undefined,
    saveLead: (lead: Lead) => Promise<Lead | null>
): Promise<boolean> {
    const formData = {
        contactId: source.contactId,
        title: `${source.name} (Cópia)`,
        value: String(source.value ?? 0),
        assignedTo: source.agent,
        source: source.origin?.trim() ? source.origin : 'Outros',
        stage: source.stage
    };
    const ok = await submitNewLeadFromModalForm(
        formData,
        contacts,
        funnelStages,
        preselectedStageId,
        saveLead
    );
    if (ok) {
        notify.success('Negociação duplicada com sucesso');
    }
    return ok;
}
